import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, AlertCircle, Plus, X, Gift, HeartHandshake } from "lucide-react";

/**
 * DiploksLowBalanceDialog — singleton.
 * Surfaces when a premium feature returns `insufficient_balance` from
 * the central engine. Offers three actions:
 *   - Top up Diploks (deep-links to the matching wallet's top-up page)
 *   - Use sponsored Diploks (placeholder until Sprint R3 grants land)
 *   - Request parent approval (placeholder, Sprint R3 minor flow)
 *
 * The dialog is event-driven via `diploks:low-balance` window events
 * fired by callers when the engine returns 402.
 */
export default function DiploksLowBalanceDialog() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const onLow = (e) => setData(e.detail || {});
    window.addEventListener("diploks:low-balance", onLow);
    return () => window.removeEventListener("diploks:low-balance", onLow);
  }, []);

  if (!data) return null;

  const close = () => setData(null);
  const cost = data.cost ?? 0;
  const have = data.balance_available ?? data.balance_before ?? 0;
  const need = Math.max(cost - have, 0);
  const scope = data.payer_wallet_scope || "personal";
  const topupLink = scope === "organisation"
    ? "/dashboard/diploks/topup"
    : "/dashboard/diploks/personal/topup";

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="diploks-low-balance-dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-5 bg-amber-50 border-b border-amber-100 relative">
          <button
            type="button"
            onClick={close}
            data-testid="diploks-low-balance-close"
            className="absolute top-3 right-3 text-amber-700 hover:text-amber-900"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-amber-200 flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-amber-800" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-bold">
                Add Diploks to continue
              </p>
              <h2
                className="text-lg font-bold tracking-tight text-amber-900"
                data-testid="diploks-low-balance-title"
              >
                {data.feature_label || data.feature_key || "This premium action"}{" "}
                needs more Diploks
              </h2>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-3">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-lg bg-slate-50 border border-slate-100 px-2 py-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Need
              </p>
              <p
                className="mt-1 text-xl font-bold text-[#0A192F] tabular-nums"
                data-testid="diploks-low-balance-need"
              >
                {cost}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-100 px-2 py-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Available
              </p>
              <p
                className="mt-1 text-xl font-bold text-[#0A192F] tabular-nums"
                data-testid="diploks-low-balance-have"
              >
                {have}
              </p>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-2 py-3">
              <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
                Short
              </p>
              <p
                className="mt-1 text-xl font-bold text-amber-900 tabular-nums"
                data-testid="diploks-low-balance-short"
              >
                {need}
              </p>
            </div>
          </div>

          <Link
            to={topupLink}
            data-testid="diploks-low-balance-topup-cta"
            onClick={close}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] font-bold text-xs uppercase tracking-wider px-4 py-2.5 rounded-md hover:bg-[#B5952F]"
          >
            <Plus className="h-3.5 w-3.5" /> Top up Diploks
          </Link>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="diploks-low-balance-sponsored"
              className="text-[11px] inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 hover:border-slate-300 text-slate-700"
              title="Available in Sprint R3 once organiser grants ship."
              disabled
            >
              <Gift className="h-3 w-3" /> Use sponsored Diploks
            </button>
            <button
              type="button"
              data-testid="diploks-low-balance-parent"
              className="text-[11px] inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 hover:border-slate-300 text-slate-700"
              title="Available in Sprint R3 once the minor approval flow ships."
              disabled
            >
              <HeartHandshake className="h-3 w-3" /> Request parent approval
            </button>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50">
          <p className="text-[10px] text-slate-500 leading-relaxed flex items-start gap-1.5">
            <Coins className="h-3 w-3 text-[#D4AF37] mt-0.5 shrink-0" />
            Diploks are Diplomexa platform credits — not cash, not crypto,
            not transferable, not withdrawable, and cannot be converted into
            money.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Fire the low-balance dialog with a detail payload. */
export function showLowBalance(payload) {
  window.dispatchEvent(
    new CustomEvent("diploks:low-balance", { detail: payload }),
  );
}
